import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { fetchCompaniesToSwitch, switchCompany } from '@/api/companies';
import type { CompanyToSwitch } from '@/api/companies/types';
import { fetchMe } from '@/api/me';
import type { UserMenuCompany } from '@/components/shared/UserMenu';
import { RETRACED_ADMIN_COMPANY_ID } from '@/lib/constants/userRoles';
import { beginImpersonation, endImpersonation } from '@/lib/utils/session';
import { useSessionStore } from '@/store/sessionStore';

/**
 * The operator's own switchable companies. Exported because a membership write
 * elsewhere in the app invalidates it — see `useUserCompanyActions`.
 */
export const COMPANIES_TO_SWITCH_QUERY_KEY = ['companies-to-switch'] as const;

/**
 * The delegated-access switcher behind both user menus.
 *
 * A user who belongs to several companies — one `user_companies` row each — can
 * act for any of them, and `/user-companies/companies-to-switch` is that list.
 * Shared because the client menu and the management menu offer the same control
 * and only differ in where the user lands afterwards; two copies of the
 * switch-then-refetch-then-clear sequence drifted the moment either changed.
 *
 * Two modes, because the same control means different things on either side:
 *
 *  - the client menu *switches* — the user already acts for one of their own
 *    companies and picks another. `onSwitched` then navigates to a page that
 *    will load under the company just selected.
 *  - the management menu *impersonates itself* — see `selfImpersonate`.
 */
export function useCompanySwitcher({
  onSwitched,
  selfImpersonate = false,
}: {
  /** Runs once the new context is in place. Never called under
   *  `selfImpersonate`: that path full-loads the customer app itself. */
  onSwitched?: () => void;
  /**
   * Treat picking a company as "act as myself, inside that company".
   *
   * A Retraced operator can hold a `user_companies` row in a customer company —
   * delegated access — and switching there is not the customer case: pinning
   * `x-retraced-company-id` alone leaves the session a *staff* session, so the
   * customer app's guard reads "staff, not impersonating" and sends them
   * straight back to the management area. Impersonating their own id in that
   * company is what makes it a customer session, and it is also what the
   * operator means by the click.
   */
  selfImpersonate?: boolean;
}) {
  const setUser = useSessionStore(s => s.setUser);
  const userId = useSessionStore(s => s.user?.id);
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const { data: companies = [] } = useQuery({
    queryKey: COMPANIES_TO_SWITCH_QUERY_KEY,
    queryFn: fetchCompaniesToSwitch,
  });

  const switchCompanyMutation = useMutation({
    mutationFn: (companyId: string) => switchCompany(companyId),
  });

  const filteredCompanies = companies.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()),
  );

  async function handleSwitchCompany(company: UserMenuCompany) {
    // Picking Retraced is a return to the operator's own context, not a switch
    // into a customer: it has to *clear* the company override rather than pin
    // one, and land back in the tools. `endImpersonation` already is that
    // operation — clear `impersonateId` and `companyId`, drop the cached
    // queries, full-load into `/management` so `/users/me` is re-read as the
    // operator — so it is reused rather than reimplemented. It
    // is correct here even with no impersonation running: the overrides it
    // clears are exactly the ones a switch sets.
    //
    // Guarded on a non-empty id: `RETRACED_ADMIN_COMPANY_ID` is env-driven and
    // defaults to `''`, which would otherwise match a company with no id.
    if (
      RETRACED_ADMIN_COMPANY_ID &&
      company.companyId === RETRACED_ADMIN_COMPANY_ID
    ) {
      endImpersonation();
      return;
    }

    // Everything the switch would do — write the company, re-read `/users/me`,
    // drop the cache, navigate — `beginImpersonation` already does, plus the
    // impersonation header that makes the session a customer one. It full-loads
    // `/dashboard`, so there is nothing to await and no `onSwitched` to run.
    if (selfImpersonate) {
      if (!userId) return;
      beginImpersonation(userId, company.companyId);
      return;
    }

    try {
      await switchCompanyMutation.mutateAsync(company.companyId);
      const me = await fetchMe();
      if (me) setUser(me);
      // The whole cache, not a subset: every entry was fetched under the old
      // company's scope, so anything left would answer for the wrong one.
      queryClient.clear();
      onSwitched?.();
    } catch {
      // switch failed — user remains on current company
    }
  }

  return {
    search,
    setSearch,
    /** Already filtered by `search` — the switcher renders what it is given. */
    companies: filteredCompanies as CompanyToSwitch[],
    /** Unfiltered: the control is offered when the user has companies at all,
     *  so a search that matches none still leaves the search box reachable. */
    hasCompanies: companies.length > 0,
    handleSwitchCompany,
  };
}
